// src/components/MobileForm.js
import React, { useState, useEffect, useRef } from "react";
import { Calendar, MapPin, Clock, Globe, ArrowLeft } from "lucide-react";
import { useFormContext } from "../context/FormContext";
import { formatDisplayDate } from "../utils/dateUtils";
import countryList from "react-select-country-list";

export const MobileForm = ({ onOpenModal, onClose }) => {
  const {
    pickup,
    dropoff,
    sameLocation,
    setSameLocation,
    pickupDate,
    dropoffDate,
    pickupTime,
    driverCountry,
    setDriverCountry,
    driverAge,
    setDriverAge,
    handleMobileSubmit,
    userManuallySelectedCountry,
    setUserManuallySelectedCountry,
    dropoffTime,
  } = useFormContext();

  const countries = countryList().getData();


  // Local UI States
  const [isAgeDefault, setIsAgeDefault] = useState(true);
  const [customAge, setCustomAge] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [localPickup, setLocalPickup] = useState("");
  const [localDropoff, setLocalDropoff] = useState("");

  useEffect(() => {
    if (pickup) {
      setLocalPickup(pickup);
    }
  }, [pickup]);

  useEffect(() => {
    if (dropoff) {
      setLocalDropoff(dropoff);
    }
  }, [dropoff]);


  useEffect(() => {
    const isDefault = driverAge === "25-70" || driverAge === "30-65";
    setIsAgeDefault(isDefault);
    if (!isDefault) {
      setCustomAge(driverAge);
    }
  }, [driverAge]);

  // Add this — runs only once when component mounts
  useEffect(() => {
    if (!driverAge || driverAge === "35") {
      setDriverAge(getDefaultAgeRange(driverCountry || "US"));
      setIsAgeDefault(true);
    }
  }, []); // ← empty array = runs once only

  useEffect(() => {
    if (driverCountry && isAgeDefault) {
      setDriverAge(getDefaultAgeRange(driverCountry));
    }
  }, [driverCountry]);

  const getCountryName = (code) => {
    const c = countries.find((x) => x.value === code);
    return c ? c.label : code;
  };

  const getDefaultAgeRange = (countryCode) => {
    const northAmerica = ["US", "CA", "MX"];
    return northAmerica.includes(countryCode) ? "25-70" : "30-65";
  };

  // ✅ CORRECT
  const handleAgeToggle = () => {
    const newVal = !isAgeDefault;
    setIsAgeDefault(newVal);
    if (newVal) {
      setDriverAge(getDefaultAgeRange(driverCountry));
    } else {
      const prefill = "35";
      setCustomAge(customAge || prefill);
      setDriverAge(customAge || prefill);
    }
  };

  // 1. Create the reference
  const countrySearchRef = React.useRef(null);

  // 2. Focus the input whenever the dropdown opens
  React.useEffect(() => {
    if (showCountryDropdown && countrySearchRef.current) {
      countrySearchRef.current.focus();
    }
  }, [showCountryDropdown]);

  const [activeIndex, setActiveIndex] = React.useState(0);
  const scrollParentRef = React.useRef(null);

  // Reset index when search term changes
  React.useEffect(() => {
    setActiveIndex(0);
  }, [countrySearchTerm]);

  // Scroll active item into view
  React.useEffect(() => {
    if (activeIndex >= 0 && scrollParentRef.current) {
      const activeItem = scrollParentRef.current.childNodes[activeIndex];
      if (activeItem) {
        activeItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex]);

  const handleInputClick = (e) => e.stopPropagation();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleMobileSubmit();
        if (onClose) onClose();
      }}
      className="flex flex-col gap-2  bg-none rounded-[1.5rem] shadow-premium border border-navy/5"
    >
      {onClose && (
        <div className="flex items-center mb-2">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-9 h-9 rounded-full border border-navy/10 bg-white shadow-sm active:scale-95 transition"
          >
            <ArrowLeft className="w-5 h-5 text-navy" />
          </button>
        </div>
      )}
      {/* 1. Locations */}
      <div
        onClick={() => onOpenModal("location")}
        className="flex items-center gap-3 p-3 border border-navy/10 rounded-xl bg-white cursor-pointer active:border-gold transition-all"
      >
        <MapPin className="text-gold w-5 h-5 shrink-0" />
        <div className="flex flex-col text-left min-w-0 flex-1">
          <span className="text-[9px] uppercase text-navy/70 font-extrabold tracking-widest leading-none mb-1">
            Pick-up
          </span>
          <span
            className={`text-sm font-bold truncate block ${localPickup ? "text-navy" : "text-navy/40"}`}
          >
            {localPickup || "Select Location"}
          </span>
        </div>
      </div>

      {!sameLocation && (
        <div
          onClick={() => onOpenModal("dropoff")}
          className="flex items-center gap-3 p-3 border border-navy/10 rounded-xl bg-white cursor-pointer active:border-gold"
        >
          <MapPin className="text-gold w-5 h-5 shrink-0" />
          <div className="flex flex-col text-left min-w-0 flex-1">
            <span className="text-[9px] uppercase text-navy/40 font-extrabold tracking-widest leading-none mb-1">
              Drop-off
            </span>
            <span
              className={`text-sm font-bold truncate ${localDropoff ? "text-navy" : "text-navy/40"}`}
            >
              {localDropoff || "Select Location"}
            </span>
          </div>
        </div>
      )}

      {/* 2. Same Location Checkbox */}
      <div className="flex items-center gap-2 px-1 py-0.5">
        <input
          type="checkbox"
          id="sameLocation"
          checked={sameLocation}
          // onChange={() => setSameLocation(!sameLocation)}
          onChange={() => {
            const nextSameLocation = !sameLocation;
            setSameLocation(nextSameLocation);

            // If we are unchecking "Same Location", clear the dropoff
            if (!nextSameLocation) {
              setLocalDropoff("");
            }
          }}
          className="w-5 h-5 appearance-none rounded border border-white checked:bg-white checked:border-transparent relative cursor-pointer"
          style={{
            // This draws a white checkmark using an SVG when the box is checked
            backgroundImage: sameLocation
              ? `url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='black' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e")`
              : "none",
            backgroundSize: "100% 100%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <label
          htmlFor="sameLocation"
          className="text-navy/60 text-[10px] font-bold text-white uppercase tracking-tight"
        >
          Return car to the same location
        </label>
      </div>

      {/* 4. Dates/Time Grid */}
      <div className="grid grid-cols-2 gap-2">
        <div
          onClick={() => onOpenModal("dates-pickup")}
          className="p-2.5 border border-navy/10 rounded-xl flex items-center gap-2.5 bg-white"
        >
          <Calendar className="text-gold w-4 h-4 shrink-0" />
          <div className="flex flex-col text-left">
            <span className="text-[9px] uppercase text-navy/70 font-extrabold tracking-widest mb-0.5">
              PICK-UP DATE
            </span>
            {/* FIXED: Changed pickupTime to pickupDate */}
            <span className="text-xs font-bold text-navy whitespace-nowrap">
              {formatDisplayDate(pickupDate)}
            </span>
          </div>
        </div>
        <div
          onClick={() => onOpenModal("time-pickup")}
          className="p-2.5 border border-navy/10 rounded-xl flex items-center gap-2.5 bg-white"
        >
          <Clock className="text-gold w-4 h-4 shrink-0" />
          <div className="flex flex-col text-left">
            <span className="text-[9px] uppercase text-navy/70 font-extrabold tracking-widest mb-0.5">
              Time
            </span>
            <span className="text-xs font-bold text-navy">
              {formatRegionalTime(pickupTime, driverCountry)}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div
          onClick={() => onOpenModal("dates-dropoff")}
          className="p-2.5 border border-navy/10 rounded-xl flex items-center gap-2.5 bg-white"
        >
          <Calendar className="text-gold w-4 h-4 shrink-0" />
          <div className="flex flex-col text-left">
            <span className="text-[9px] uppercase text-navy/70 font-extrabold tracking-widest mb-0.5">
              DROP-OFF DATE
            </span>
            <span className="text-xs font-bold text-navy whitespace-nowrap">
              {formatDisplayDate(dropoffDate)}
            </span>
          </div>
        </div>
        <div
          onClick={() => onOpenModal("time-dropoff")}
          className="p-2.5 border border-navy/10 rounded-xl flex items-center gap-2.5 bg-white"
        >
          <Clock className="text-gold w-4 h-4 shrink-0" />
          <div className="flex flex-col text-left">
            <span className="text-[9px] uppercase text-navy/70 font-extrabold tracking-widest mb-0.5">
              Time
            </span>
            <span className="text-xs font-bold text-navy">
              {formatRegionalTime(dropoffTime, driverCountry)}
            </span>
          </div>
        </div>
      </div>

      {/* 4. Residency Section - FIXED: Removed warning modal logic */}
      <div className="relative p-3 border border-navy/10 rounded-xl bg-white">
        <div
          className="flex items-center justify-between"
          onClick={() => setShowCountryDropdown(!showCountryDropdown)}
        >
          <div className="flex items-center gap-3">
            <Globe className="text-gold w-4 h-4" />
            <span className="text-navy text-[10px] font-bold uppercase tracking-tight">
              Residence:{" "}
              <span className="text-gold ml-1">
                {getCountryName(driverCountry)}
              </span>
            </span>
          </div>
          <span className="text-navy/30 text-xs">▼</span>
        </div>

        {showCountryDropdown && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-navy/10 rounded-xl shadow-2xl z-[100] p-2">
            <input
              ref={countrySearchRef}
              type="text"
              placeholder="Search country..."
              value={countrySearchTerm}
              onChange={(e) => setCountrySearchTerm(e.target.value)}
              onClick={handleInputClick}
              className="w-full border border-navy/10 rounded-lg px-3 py-2 mb-2 text-sm outline-none focus:border-gold"
            />
            <div ref={scrollParentRef} className="max-h-48 overflow-y-auto">
              {countries
                .filter((c) =>
                  c.label
                    .toLowerCase()
                    .includes(countrySearchTerm.toLowerCase()),
                )
                .map((c) => (
                  <div
                    key={c.value}
                    onClick={() => {
                      // Update immediately without modal
                      setDriverCountry(c.value);
                      setUserManuallySelectedCountry(true);
                      setShowCountryDropdown(false);
                    }}
                    className="px-3 py-2 hover:bg-navy/5 text-sm font-bold text-navy border-b border-navy/5"
                  >
                    {c.label}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Age Selection */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between p-3 border border-navy/10 rounded-xl bg-white">
          <div
            className="flex items-center gap-3 cursor-pointer select-none"
            onClick={handleAgeToggle}
          >
            <div className="relative flex items-center justify-center">
              <div
                className={`w-5 h-5 rounded border-[1.5px] transition-all duration-200 flex items-center justify-center ${isAgeDefault ? "bg-navy border-navy" : "bg-white border-gray-300"}`}
              >
                {isAgeDefault && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-navy text-[10px] font-bold uppercase tracking-tight">
              Age between <span className="text-gold">{getDefaultAgeRange(driverCountry)}</span>?
            </span>
          </div>

          {!isAgeDefault && (
            <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-200">
              <span className="text-navy text-[9px] font-bold uppercase tracking-tighter">
                Age:
              </span>
              <input
                type="number"
                min="18"
                max="99"
                value={driverAge}
                // onChange={(e) => {
                //   setCustomAge(e.target.value);
                //   setDriverAge(e.target.value);
                // }}
                onChange={(e) => setDriverAge(e.target.value)}
                className="w-10 h-6 bg-gray-50 border border-navy/10 rounded text-center text-navy font-bold text-xs focus:border-gold outline-none transition-all"
              />
            </div>
          )}
        </div>
      </div>

      <button
        type="submit"
        className="w-full bg-gold text-white py-3.5 rounded-xl font-heading font-bold uppercase tracking-widest text-sm shadow-md mt-1"
      >
        Search
      </button>
    </form>
  );
};

export const formatRegionalTime = (timeStr, countryCode) => {
  if (!timeStr) return "";
  const use12Hour = countryCode === "US" || countryCode === "CA";
  if (use12Hour) {
    const [hours, minutes] = timeStr.split(":");
    const h = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
    return `${h}:${minutes} ${ampm}`;
  }
  return timeStr;
};
